import {useCallback, useState} from 'react'
import {apiConstants, post, razorPayID} from '../common/constants'
import {useSnackbar} from 'notistack'
/**
 * Custom hook for wallet payment functionality
 * Handles Razorpay integration for wallet recharge
 *
 * @param {Object} merchant - Merchant object containing id and other details
 * @param {Function} refetchWallet - Callback to refresh wallet data after payment
 * @returns {Object} - { loading, makePayment }
 */
export function useWallet(merchant, onSuccess) {
  const [loading, setLoading] = useState(false)
  const {enqueueSnackbar} = useSnackbar()
  /**
   * Verifies payment with backend after successful Razorpay payment
   */

  const verifyPayment = useCallback(
    async (razorpayResponse, paymentData, walletType) => {
      const payload = {
        payment_id: razorpayResponse.razorpay_payment_id,
        order_id: razorpayResponse.razorpay_order_id,
        wallet_type: walletType,
        currency: paymentData.currency,
        amount: paymentData.amount,
      }

      setLoading(true)

      try {
        await post(`${apiConstants.BASE_LOCAL}${apiConstants.VERIFY_PAYMENT}`, payload)
        onSuccess()
        enqueueSnackbar('Your wallet has been successfully recharged', {
          variant: 'success',
        })
      } catch (error) {
        console.error('Payment verification failed:', error)
      } finally {
        setLoading(false)
      }
    },
    [onSuccess, enqueueSnackbar]
  )

  /**
   * Dynamically loads Razorpay script
   */
  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      // Check if script already exists
      if (window.Razorpay) {
        resolve(true)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://checkout.razorpay.com/v1/checkout.js'
      script.onload = () => resolve(true)
      script.onerror = () => resolve(false)
      document.body.appendChild(script)
    })
  }, [])

  /**
   * Initiates Razorpay payment flow
   */
  const initiateRazorpayPayment = useCallback(
    async (paymentData) => {
      const merchantDetails = merchant

      // Load Razorpay script
      const isScriptLoaded = await loadRazorpayScript()
      if (!isScriptLoaded) {
        console.error('Razorpay SDK failed to load. Please check your internet connection.')
        return
      }

      const {amount, id: orderId, currency} = paymentData

      const razorpayOptions = {
        key: razorPayID,
        amount: amount.toString(),
        currency,
        name: merchantDetails.merchantName || 'Proship',
        description: 'Wallet Recharge - Proship',
        image: '', // Could be merchant logo
        order_id: orderId,
        handler: async (response) => {
          await verifyPayment(response, paymentData, merchantDetails.walletType)
        },
        prefill: {
          name: merchantDetails.merchantName || '',
          email: merchantDetails.merchantEmail || '',
        },
        theme: {
          color: '#61dafb',
        },
        modal: {
          ondismiss: () => {
            setLoading(false)
            console.log('Payment modal dismissed')
          },
        },
      }

      const paymentObject = new window.Razorpay(razorpayOptions)
      paymentObject.open()
    },
    [merchant, verifyPayment, loadRazorpayScript]
  )

  /**
   * Creates payment order and initiates payment flow
   */
  const makePayment = useCallback(
    async (amount) => {
      if (!amount || amount <= 0) {
        console.error('Invalid amount provided')
        return
      }

      if (!merchant?.merchantRef) {
        console.error('Merchant information is required')
        return
      }

      const payload = {
        merchantOId: merchant.merchantRef,
        amount: parseInt(amount, 10),
        currency: 'INR',
      }

      setLoading(true)

      try {
        const response = await post(
          `${apiConstants.BASE_LOCAL}${apiConstants.ADD_WALLET_BALANCE}`,
          payload
        )

        await initiateRazorpayPayment(response.data)
      } catch (error) {
        enqueueSnackbar(error.response?.data?.message || 'Failed to create payment order', {
          variant: 'error',
        })
        console.error('Failed to create payment order:', error)
        setLoading(false)
      }
    },
    [merchant, initiateRazorpayPayment, enqueueSnackbar]
  )

  return {
    loading,
    makePayment,
  }
}
